
// @ts-nocheck

import { Component, Vue, Watch } from 'vue-property-decorator';
import { State } from 'vuex-class';

import { clone, each, pullAt, cloneDeep, forEach, isEmpty, groupBy } from 'lodash';

import SectionHeading from '@/components/heading/SectionHeading.vue';
import SectionH1 from '@/components/heading/SectionH1.vue';
import AnnouncementPreview from '@/components/announcement/Announcement.vue';

import { getAnnouncement, createAnnouncement, updateAnnouncement } from '@/services/axios';
import { Announcement } from '@/types/index.d';

@Component({
  components: {
    SectionHeading,
    SectionH1,
    AnnouncementPreview,
  },
})
export default class AnnouncementView extends Vue {
  @State opId!: number;

  ancmText: string = '';
  previousAncmText: string = '';
  textAreaMaxHeight: number = 64;
  isEditing: boolean = true;
  isTextLimitReached: boolean = false;
  isLoading: boolean = true;
  isSaving: boolean = false;
  isFetchingError: boolean = false;
  textLimitLength: number = 100000;
  livePreviewAncmText: string = '';
  livePreviewTimeout: any = null;
  isPreviewReady: boolean = true;
  currentAncmMsgId: string | null = null;
  popupState: string | 'saving' | 'success' | 'error' | 'none' = 'none';

  announcements: Announcement[] = [
    {
      tag_id: 'Default',
      message: '',
      time_table_json: '',
      is_tag_error: false,
    },
  ];

  get canUseMessageTag() {
    return this.opId === 67; // Only for Op 67 กรมเจ้าท่า
  }

  get textOverLimitMessage() {
    const amount = this.ancmText.length - this.textLimitLength;
    return `จำนวนตัวอักษรเกินกำหนดไป ${amount} ตัว กรุณาลดจำนวนตัวอักษร`;
  }

  get highlightedAncmText() {
    if (this.isTextLimitReached) {
      let text = this.ancmText.substring(0, this.textLimitLength);
      text = `${text}<mark style="background: #f9b9c4; color: transparent;">${this.ancmText.substring(
        this.textLimitLength
      )}</mark>`;
      return text;
    }
    return `${this.ancmText}`;
  }

  get popupStateTitle() {
    if (this.popupState === 'saving') {
      return 'กำลังบันทึก';
    } else if (this.popupState === 'success') {
      return 'สำเร็จ';
    } else if (this.popupState === 'error') {
      return 'ขออภัย';
    }
    return null;
  }

  get popupStateDetail() {
    if (this.popupState === 'success') {
      return this.canUseMessageTag
        ? 'บันทึกประกาศแล้ว'
        : this.ancmText.trim().length
        ? 'ประกาศขึ้นบนจอแล้ว'
        : 'ลบประกาศแล้ว';
    } else if (this.popupState === 'error') {
      return 'กรุณาลองอีกครั้ง';
    }
    return null;
  }

  get popupStateIconName() {
    if (this.popupState === 'success') {
      return 'ic-complete';
    } else if (this.popupState === 'error') {
      return 'ic-fail';
    }
    return null;
  }

  @Watch('ancmText')
  onAncmTextChanged() {
    this.isTextLimitReached = this.ancmText.length > this.textLimitLength;
    this.isPreviewReady = false;
    setTimeout(() => {
      this.livePreviewAncmText = this.ancmText;
      this.isPreviewReady = true;
    }, 0);
  }

  @Watch('isEditing')
  onIsEditingChanged() {
    if (this.isEditing) {
      this.onWindowResized();
    }
  }

  onWindowResized() {
    const ref: any = this.$refs.ancmContainer;
    if (ref) {
      if (!this.canUseMessageTag) {
        this.textAreaMaxHeight = ref.clientHeight - 104 - 8 - 36 - 40 - 24;
      }
    }
  }

  onAncmTextAreaScroll(e: any) {
    const textAreaRef: any = this.$refs.ancmTextArea;
    const backdropRef: any = this.$refs.backdropAncm;
    if (textAreaRef && textAreaRef.$el && backdropRef) {
      backdropRef.scrollTop = e.target.scrollTop;
    }
  }

  handleClickClearText() {
    this.ancmText = '';
  }

  handleClickCancelEditing() {
    this.ancmText = clone(this.previousAncmText);
    this.isEditing = false;
  }

  onSaveFinished(shouldFetch: boolean) {
    setTimeout(() => {
      if (shouldFetch) {
        this.fetchAnnouncement();
      }
      this.popupState = 'none';
      this.isSaving = false;
    }, 1.5 * 1000);
  }

  handleClickSave() {
    if (this.canUseMessageTag) {
      let hasError: boolean = false;
      let hasDuplicateTag: boolean = false;

      // Find duplicate tag_id and mark its error
      const tagIdAncmGrouped = groupBy(this.announcements, 'tag_id');
      forEach(tagIdAncmGrouped, (ancms: Announcement[]) => {
        if (ancms.length > 1) {
          hasDuplicateTag = true;
          ancms.forEach((ancm: Announcement) => {
            ancm.is_tag_error = true;
          });
        } else {
          ancms.forEach((ancm: Announcement) => {
            ancm.is_tag_error = false;
          });
        }
      });

      forEach(this.announcements, (ancm: Announcement) => {
        if (isEmpty(ancm.tag_id.trim())) {
          ancm.is_tag_error = true;
          hasError = true;
        }
      });

      if (hasError || hasDuplicateTag) {
        return;
      }

      let arrayToSave: any[] = [];
      forEach(this.announcements, (ancm: Announcement) => {
        arrayToSave.push({
          tag_id: ancm.tag_id,
          message: ancm.message,
          time_table_json: ancm.time_table_json,
        });
      });

      const stringifyMessage = JSON.stringify(arrayToSave);

      if (this.currentAncmMsgId) {
        updateAnnouncement(this.opId, this.currentAncmMsgId, stringifyMessage)
          .then(() => {
            this.popupState = 'success';
            this.onSaveFinished(true);
          })
          .catch(err => {
            this.popupState = 'error';
            this.showToastError(null, err);
            this.onSaveFinished(false);
          });
      } else {
        createAnnouncement(this.opId, stringifyMessage)
          .then(() => {
            this.popupState = 'success';
            this.onSaveFinished(true);
          })
          .catch(err => {
            this.popupState = 'error';
            this.showToastError(null, err);
            this.onSaveFinished(false);
          });
      }
    } else {
      this.isSaving = true;
      this.popupState = 'saving';
      if (this.currentAncmMsgId) {
        updateAnnouncement(this.opId, this.currentAncmMsgId, this.ancmText)
          .then(() => {
            this.popupState = 'success';
            this.onSaveFinished(true);
          })
          .catch(err => {
            this.popupState = 'error';
            this.showToastError(null, err);
            this.onSaveFinished(false);
          });
      } else {
        createAnnouncement(this.opId, this.ancmText)
          .then(() => {
            this.popupState = 'success';
            this.onSaveFinished(true);
          })
          .catch(err => {
            this.popupState = 'error';
            this.showToastError(null, err);
            this.onSaveFinished(false);
          });
      }
    }
  }

  handleClickEditAncm() {
    this.isEditing = true;
  }

  handleClickAdd() {
    this.announcements.push({
      tag_id: '',
      message: '',
      time_table_json: '',
      is_tag_error: false,
    });
  }

  handleClickDelete(index: number) {
    const newAnnouncements = cloneDeep(this.announcements);
    pullAt(newAnnouncements, index);
    this.announcements = newAnnouncements;
  }

  fetchAnnouncement() {
    if (!this.canUseMessageTag) {
      this.isEditing = false;
    }

    this.isLoading = true;
    getAnnouncement(this.opId)
      .then(res => {
        const data = res.data || [];
        const messagesObj = data[0];
        if (this.canUseMessageTag) {
          this.announcements = [
            {
              tag_id: 'Default',
              message: '',
              time_table_json: '',
              is_tag_error: false,
            },
          ];

          if (messagesObj) {
            this.currentAncmMsgId = messagesObj.stm_id;
            const message = messagesObj.messages[0];
            if (message && message.msg) {
              try {
                const jsonArray = JSON.parse(message.msg);
                if (Array.isArray(jsonArray)) {
                  const newAnnouncements: Announcement[] = [];
                  each(jsonArray, (obj, index) => {
                    if (index === 0) {
                      newAnnouncements.push({
                        tag_id: 'Default',
                        message: obj.message,
                        time_table_json: '',
                        is_tag_error: false,
                      });
                    } else {
                      newAnnouncements.push(obj);
                    }
                  });
                  this.announcements = newAnnouncements;
                }
              } catch (e) {
                this.announcements = [
                  {
                    tag_id: 'Default',
                    message: message.msg,
                    time_table_json: '',
                    is_tag_error: false,
                  },
                ];
              }
            }
          }
        } else {
          if (messagesObj) {
            this.currentAncmMsgId = messagesObj.stm_id;
            const message = messagesObj.messages[0];
            if (message) {
              this.previousAncmText = message.msg;
              this.ancmText = clone(this.previousAncmText);
              this.livePreviewAncmText = this.ancmText;
            }
          }
        }

        this.isLoading = false;
        this.isFetchingError = false;
      })
      .catch(err => {
        this.isLoading = false;
        this.isFetchingError = true;
        this.showToastError(null, err);
      });
  }

  showToastError(message: string | null, error: any = null) {
    var msg = message || 'Unexpected error occured. Please try again.';
    if (error) {
      msg = `${msg} (${error.response.error.status})`;
    }
    this.$toasted.show(msg, { type: 'error' });
  }

  mounted() {
    window.addEventListener('resize', this.onWindowResized);
    this.fetchAnnouncement();

    const ref: any = this.$refs.ancmTextArea;
    if (ref && ref.$el) {
      ref.$el.onscroll = this.onAncmTextAreaScroll;
    }
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResized);
  }
}
